import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const CATALOG_PAGE_SIZE = 250;

/**
 * Displays a random available catalog product and adds it without leaving the cart drawer.
 */
class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.#loadRandomProduct();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-upsell-add]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();
    this.#addProduct(button);
  };

  async #loadRandomProduct() {
    const productCount = Number(this.dataset.productCount) || 0;
    if (!productCount) return;

    try {
      const randomIndex = this.#randomInteger(productCount);
      const page = Math.floor(randomIndex / CATALOG_PAGE_SIZE) + 1;
      const catalogRoot = window.Shopify?.routes?.root || '/';
      const productsUrl = `${catalogRoot}collections/all/products.json?limit=${CATALOG_PAGE_SIZE}&page=${page}`;

      const [productsResponse, cartResponse] = await Promise.all([
        fetch(productsUrl, { credentials: 'same-origin', cache: 'no-store' }),
        fetch(`${Theme.routes.cart_url}.json`, { credentials: 'same-origin', headers: { Accept: 'application/json' } }),
      ]);

      if (!productsResponse.ok) throw new Error(`Catalog request failed: ${productsResponse.status}`);

      const productsData = await productsResponse.json();
      const cartData = cartResponse.ok ? await cartResponse.json() : { items: [] };
      const cartProductIds = new Set((cartData.items || []).map((item) => String(item.product_id)));
      const products = Array.isArray(productsData.products) ? productsData.products : [];
      const availableProducts = products.filter((product) => product.variants?.some((variant) => variant.available));
      const productsNotInCart = availableProducts.filter((product) => !cartProductIds.has(String(product.id)));
      const candidates = productsNotInCart.length ? productsNotInCart : availableProducts;

      if (!candidates.length) return;

      const product = candidates[this.#randomInteger(candidates.length)];
      const variant = product.variants.find((item) => item.available);
      if (!variant) return;

      this.#renderProduct(product, variant);
    } catch (error) {
      // The Liquid-rendered product remains usable as a resilient fallback.
      console.warn('[cart-upsell] Could not refresh random product:', error);
    }
  }

  #renderProduct(product, variant) {
    const productContainer = this.querySelector('[data-upsell-product]');
    if (!(productContainer instanceof HTMLElement)) return;

    const productUrl = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle)}?variant=${variant.id}`;
    const imageUrl = variant.featured_image?.src || product.images?.[0]?.src || '';

    let imageLink = productContainer.querySelector('.cart-upsell__image-link');
    if (!(imageLink instanceof HTMLAnchorElement)) {
      imageLink = document.createElement('a');
      imageLink.className = 'cart-upsell__image-link';
      imageLink.dataset.upsellLink = '';
      productContainer.prepend(imageLink);
    }

    imageLink.href = productUrl;
    imageLink.setAttribute('aria-label', `Ver ${product.title}`);
    imageLink.replaceChildren();

    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'cart-upsell__image';
      image.src = this.#sizedImageUrl(imageUrl, 180);
      image.alt = product.title;
      image.loading = 'lazy';
      image.width = 90;
      image.height = 113;
      imageLink.append(image);
    }

    let details = productContainer.querySelector('.cart-upsell__details');
    if (!(details instanceof HTMLElement)) {
      details = document.createElement('div');
      details.className = 'cart-upsell__details';
      productContainer.append(details);
    }

    details.replaceChildren();

    const title = document.createElement('a');
    title.className = 'cart-upsell__title';
    title.dataset.upsellLink = '';
    title.href = productUrl;
    title.textContent = product.title;

    const price = document.createElement('span');
    price.className = 'cart-upsell__price';
    price.dataset.upsellPrice = '';
    price.textContent = this.#formatPrice(variant.price);

    details.append(title, price);

    productContainer.querySelector('[data-upsell-add]')?.remove();

    const button = document.createElement('button');
    button.className = 'cart-upsell__button';
    button.type = 'button';
    button.dataset.upsellAdd = '';

    const plus = document.createElement('span');
    plus.setAttribute('aria-hidden', 'true');
    plus.textContent = '+';

    const buttonText = document.createElement('span');
    buttonText.dataset.upsellButtonText = '';
    buttonText.textContent = this.dataset.buttonLabel || 'Agregar';
    button.append(plus, buttonText);

    productContainer.append(button);
    this.dataset.productId = String(product.id);
    this.dataset.variantId = String(variant.id);
    this.classList.remove('cart-upsell--empty');
  }

  async #addProduct(button) {
    const variantId = this.dataset.variantId;
    if (!variantId || button.disabled) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Agregando…';

    const isCustomCartDrawer = Boolean(this.closest('[id^="CartDrawer-"]'));
    const cartComponents = [...document.querySelectorAll('cart-items-component[data-section-id]')];
    const sectionIds = [...new Set(cartComponents.map((component) => component.dataset.sectionId).filter(Boolean))];
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: isCustomCartDrawer ? 'update' : 'add',
        context: isCustomCartDrawer ? 'custom-cart-upsell' : 'cart-upsell',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: `${window.location.pathname}${window.location.search}`,
        }),
      });
      const responseData = await response.json();

      if (!response.ok || responseData.status) {
        throw new Error(responseData.description || responseData.message || 'No se pudo agregar el producto.');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('No se pudo actualizar el carrito.');

      const cart = await cartResponse.json();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          itemCount: 1,
          items: cart.items,
          productId: this.dataset.productId,
          sections: responseData.sections,
          source: 'cart-upsell',
          sourceId: this.dataset.productId,
        },
      });

      this.#setStatus('Producto agregado al carrito.');

      if (isCustomCartDrawer) {
        window.dispatchEvent(new CustomEvent('cart:updated'));
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo agregar el producto.';
      deferredEventPromise.resolve({
        detail: { didError: true, itemCount: 0, source: 'cart-upsell' },
      });
      this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      this.#setStatus(message);
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  #setStatus(message) {
    const status = this.querySelector('[data-upsell-status]');
    if (status) status.textContent = message;
  }

  #formatPrice(price) {
    const amount = Number(price);
    if (!Number.isFinite(amount)) return String(price || '');

    try {
      return new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'es', {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      }).format(amount);
    } catch {
      return String(price);
    }
  }

  #sizedImageUrl(source, width) {
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch {
      return source;
    }
  }

  #randomInteger(maximum) {
    if (maximum <= 1) return 0;
    const randomValues = new Uint32Array(1);
    crypto.getRandomValues(randomValues);
    return randomValues[0] % maximum;
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
